import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  StoryVectorMemoryQuery,
  StoryVectorMemoryRecord,
  StoryVectorMemoryRecordInput,
  StoryVectorMemoryState,
} from '@/IsekaiRPG/modules/history/historyTypes';
import {
  StoryVectorMemoryRecordInputSchema,
  StoryVectorMemoryStateSchema,
} from '@/IsekaiRPG/modules/history/historyTypes';

function createRecordId(): string {
  return `story_vector_memory_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
}

function normalizeRecordInput(input: StoryVectorMemoryRecordInput): StoryVectorMemoryRecord {
  const parsed = StoryVectorMemoryRecordInputSchema.parse(input);
  return {
    ...parsed,
    id: parsed.id || createRecordId(),
    createdAt: parsed.createdAt ?? Date.now(),
  };
}

function sortRecords(records: StoryVectorMemoryRecord[]): StoryVectorMemoryRecord[] {
  return [...records].sort((left, right) => {
    if (left.endedAt !== right.endedAt) return left.endedAt - right.endedAt;
    if (left.createdAt !== right.createdAt) return left.createdAt - right.createdAt;
    return left.id.localeCompare(right.id);
  });
}

function applyMaxEntries(records: StoryVectorMemoryRecord[], maxEntries: number): StoryVectorMemoryRecord[] {
  if (records.length <= maxEntries) return records;
  return records.slice(records.length - maxEntries);
}

export const useStoryVectorMemoryStore = defineStore('storyVectorMemory', () => {
  const state = ref<StoryVectorMemoryState>(
    StoryVectorMemoryStateSchema.parse({
      records: [],
    }),
  );

  const records = computed(() => state.value.records);

  function setMaxEntries(maxEntries: number): void {
    state.value.maxEntries = Math.max(1, Math.trunc(maxEntries));
    state.value.records = applyMaxEntries(sortRecords(state.value.records), state.value.maxEntries);
  }

  function replaceRecords(nextRecords: StoryVectorMemoryRecordInput[]): void {
    const normalized = nextRecords.map(record => normalizeRecordInput(record));
    state.value.records = applyMaxEntries(sortRecords(normalized), state.value.maxEntries);
  }

  function appendRecord(input: StoryVectorMemoryRecordInput): StoryVectorMemoryRecord {
    const created = normalizeRecordInput(input);
    state.value.records = applyMaxEntries(sortRecords([...state.value.records, created]), state.value.maxEntries);
    return created;
  }

  function appendRecords(inputs: StoryVectorMemoryRecordInput[]): StoryVectorMemoryRecord[] {
    const created = inputs.map(input => normalizeRecordInput(input));
    state.value.records = applyMaxEntries(sortRecords([...state.value.records, ...created]), state.value.maxEntries);
    return created;
  }

  function getRecord(recordId: string): StoryVectorMemoryRecord | null {
    return state.value.records.find(record => record.id === recordId) ?? null;
  }

  function removeRecord(recordId: string): boolean {
    const before = state.value.records.length;
    state.value.records = state.value.records.filter(record => record.id !== recordId);
    return state.value.records.length !== before;
  }

  function clearRecords(): void {
    state.value.records = [];
  }

  function queryRecords(query: StoryVectorMemoryQuery = {}): StoryVectorMemoryRecord[] {
    const {
      sourceSummaryEntryId,
      sourceArchiveRecordId,
      sourceSessionKey,
      sceneName,
      participantId,
      sourceMessageId,
      contentKind,
      keyword,
      limit,
    } = query;

    let result = state.value.records.filter(record => {
      if (sourceSummaryEntryId && record.sourceSummaryEntryId !== sourceSummaryEntryId) return false;
      if (sourceArchiveRecordId && record.sourceArchiveRecordId !== sourceArchiveRecordId) return false;
      if (sourceSessionKey && record.sourceSessionKey !== sourceSessionKey) return false;
      if (sceneName && record.sceneName !== sceneName) return false;
      if (participantId && !record.participantIds.includes(participantId)) return false;
      if (sourceMessageId && !record.sourceMessageIds.includes(sourceMessageId)) return false;
      if (contentKind && record.contentKind !== contentKind) return false;
      if (keyword && !record.content.includes(keyword)) return false;
      return true;
    });

    result = sortRecords(result);
    if (limit && result.length > limit) {
      result = result.slice(result.length - limit);
    }

    return result;
  }

  return {
    state,
    records,
    setMaxEntries,
    replaceRecords,
    appendRecord,
    appendRecords,
    getRecord,
    removeRecord,
    clearRecords,
    queryRecords,
  };
});
